import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'Hooks/useAuth';
import { useNotification } from 'Hooks/useNotification';
import { useFormValidation } from 'Hooks/useFormValidation';
import { useCountdown } from 'Hooks/useCountdown';
import { ROUTES } from 'Utils/routes';
import { ConfirmForgotPasswordFormErrors } from 'Types/forms';
import { isValidEmail } from 'Utils/validation';
import Copyright from 'Components/Copyright/Copyright';
import FormTextField from 'Components/FormTextField/FormTextField';
import PasswordErrorList from 'Components/PasswordErrorList/PasswordErrorList';
import PasswordStrengthIndicator from 'Components/PasswordStrengthIndicator/PasswordStrengthIndicator';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';

export default function ConfirmForgotPassword() {
  const { email, forgotPassword, forgotPasswordSubmit } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const {
    values,
    errors,
    setFieldValue,
    setFieldError,
    clearFieldError,
    validateEmail,
    validatePassword,
    validateRequired,
    validateAll,
  } = useFormValidation<ConfirmForgotPasswordFormErrors>({ email, password: '', code: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const { secondsLeft, isActive: isCooldown, start: startCooldown } = useCountdown(30);

  // Clear password when navigating away for security
  useEffect(() => {
    return () => {
      setFieldValue('password', '');
    };
  }, [setFieldValue]);

  const validateForm = (): boolean => {
    return validateAll([
      () => validateEmail('email'),
      () => validatePassword('password'),
      () => validateRequired('code', 'Verification code is required'),
    ]);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    const result = await forgotPasswordSubmit(values.email, values.code, values.password);
    setIsLoading(false);

    if (result.success) {
      navigate(ROUTES.SIGN_IN, { replace: true });
    } else {
      showNotification(result.message);
    }
  };

  const handleResendCode = async () => {
    if (!values.email.trim() || !isValidEmail(values.email)) {
      setFieldError('email', 'Please enter a valid email address');
      return;
    }
    setIsResending(true);
    const result = await forgotPassword(values.email);
    setIsResending(false);
    if (result.success) {
      startCooldown();
      showNotification('Verification code sent');
    } else {
      showNotification(result.message);
    }
  };

  return (
    <Container component='main' maxWidth='xs'>
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.dark' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component='h1' variant='h5'>
          Reset Password
        </Typography>
        <Typography variant='body2' align='center'>
          Enter your new password and verification code sent to your email address
        </Typography>
        <Box component='form' noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormTextField
                required
                name='email'
                label='Email Address'
                value={values.email}
                fieldError={errors.email}
                disabled={isLoading}
                onFieldChange={setFieldValue}
                onClearError={clearFieldError}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                color='info'
                fullWidth
                name='password'
                label='New Password'
                type='password'
                id='password'
                autoFocus
                value={values.password}
                error={!!errors.password}
                disabled={isLoading}
                inputProps={{
                  'aria-describedby': errors.password ? 'password-errors' : undefined,
                }}
                onChange={(e) => {
                  setFieldValue('password', e.target.value);
                  if (errors.password) clearFieldError('password');
                }}
              />
              <PasswordStrengthIndicator password={values.password} />
              <PasswordErrorList errors={errors.password || []} id='password-errors' />
            </Grid>
            <Grid item xs={12}>
              <FormTextField
                required
                name='code'
                label='Verification Code'
                value={values.code}
                fieldError={errors.code}
                disabled={isLoading}
                onFieldChange={setFieldValue}
                onClearError={clearFieldError}
              />
            </Grid>
          </Grid>
          <Button
            type='submit'
            color='secondary'
            fullWidth
            variant='contained'
            disabled={isLoading}
            sx={{ mt: 3, mb: 2 }}
          >
            {isLoading ? <CircularProgress size={24} color='inherit' /> : 'Reset Password'}
          </Button>
          <Grid container justifyContent='space-between' alignItems='center'>
            <Grid item>
              <Button
                variant='text'
                color='info'
                disabled={isResending || isLoading || isCooldown}
                onClick={handleResendCode}
              >
                {isResending ? (
                  <CircularProgress size={20} color='inherit' />
                ) : isCooldown ? (
                  `Resend code (${secondsLeft}s)`
                ) : (
                  'Resend code'
                )}
              </Button>
            </Grid>
            <Grid item>
              <Link href={ROUTES.SIGN_IN} variant='body2' color='secondary.contrastText'>
                Remember password? Sign in
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Copyright sx={{ mt: 5 }} />
    </Container>
  );
}
